import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

/**
 * AdditionalPagesController
 *
 * Manages the Additional Pages tab of a report. Supports two page types:
 *   - gallery:   Drag & drop image upload zone with a thumbnail grid (max 9 images).
 *   - rich_text: Trix rich text editor with optional single image.
 *
 * Save model: manual only. The "Save Content" button is the sole save trigger,
 * except when the user switches pages while having unsaved changes — they are
 * asked to save or discard before switching.
 */
export default class extends Controller {
  // ====================================================================
  // STIMULUS CONFIGURATION
  // ====================================================================

  static targets = [
    "sidebar",
    "pageList",
    "editor",
    "titleInput",
    "contentInput",
    "trixEditor",
    "saveStatus",
    "pageItem",
    "newPageBtn",
    "pageTypeSelector",
    "galleryEditor",
    "richTextEditor",
    "galleryDropZone",
    "galleryUploadInput",
    "galleryUploadProgress",
    "galleryUploadStatus",
    "galleryImages",
    "layoutSelector",
    "saveGroup",
    "titleGroup"
  ]

  static values = {
    taskId: Number,
    reportId: Number,
    currentPageId: Number,
    hasUnsavedChanges: { type: Boolean, default: false }
  }

  // ====================================================================
  // LIFECYCLE
  // ====================================================================

  connect() {
    /** @type {Array<Object>} Local copy of all pages for the report */
    this.pages = []

    /** @type {Object|null} Full data object for the currently active page */
    this.currentPage = null

    /**
     * Guard flag: true while we are programmatically loading content into
     * the Trix editor to prevent trix-change events from marking the page dirty.
     */
    this.isLoadingContent = false

    this.loadPages()
    this.initializeSortable()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  // ====================================================================
  // DATA LOADING
  // ====================================================================

  /** Fetch all pages from the server and populate the sidebar. */
  async loadPages() {
    try {
      this.setStatus("Loading pages...", "info")

      const response = await fetch(this.pagesIndexUrl(), { headers: this.headers() })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      this.pages = data.pages || []

      this.renderPageList()

      if (this.pages.length > 0) {
        await this.loadPageContent(this.pages[0].id)
      } else {
        this.showEmptyState()
      }

      this.setStatus("", "")
    } catch (error) {
      console.error("[additional-pages] Failed to load pages:", error)
      this.setStatus(`Failed to load pages: ${error.message}`, "error")
    }
  }

  /**
   * Fetch a single page's full data from the server and render the editor.
   *
   * @param {number} pageId
   */
  async loadPageContent(pageId) {
    try {
      const response = await fetch(this.pageShowUrl(pageId), { headers: this.headers() })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()

      this.currentPage = data
      this.currentPageIdValue = pageId
      this.hasUnsavedChangesValue = false

      this.renderEditor()
      this.updateActivePageItem(pageId)
      this.dispatch("changed", { detail: { hasUnsavedChanges: false } })
    } catch (error) {
      console.error("[additional-pages] Failed to load page content:", error)
      this.setStatus(`Failed to load page: ${error.message}`, "error")
    }
  }

  // ====================================================================
  // SIDEBAR RENDERING
  // ====================================================================

  /**
   * Build the page list HTML in the sidebar.
   * Called after initial load and after any structural change (create/delete/reorder).
   */
  renderPageList() {
    if (!this.hasPageListTarget) return

    const pageTypeIcon = (type) =>
      type === "gallery"
        ? '<i class="fa fa-images" style="color: #5b73e8; font-size: 12px; margin-right: 6px;"></i>'
        : '<i class="fa fa-file-text" style="color: #6c757d; font-size: 12px; margin-right: 6px;"></i>'

    this.pageListTarget.innerHTML = this.pages.map(page => `
      <div class="additional-page-item ${this.currentPageIdValue === page.id ? "active" : ""}"
           data-page-id="${page.id}"
           data-additional-pages-target="pageItem"
           data-action="click->additional-pages#handlePageClick">
        <div class="page-drag-handle" title="Drag to reorder">
          <i class="fa fa-bars"></i>
        </div>
        <div class="page-info">
          <div class="page-title">
            ${pageTypeIcon(page.page_type)}
            ${this.escapeHtml(page.title || "Untitled")}
          </div>
        </div>
        <button type="button"
                class="btn-delete-page"
                data-page-id="${page.id}"
                data-action="click->additional-pages#deletePage:stop"
                title="Delete page">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    `).join("")

    // Re-initialise sortable handles after a full list re-render.
    this.initializeSortable()
  }

  /**
   * Update the active/inactive highlight in the sidebar for a given page,
   * without re-rendering the whole list.
   *
   * @param {number} pageId
   */
  updateActivePageItem(pageId) {
    this.pageItemTargets.forEach(item => {
      item.classList.toggle("active", parseInt(item.dataset.pageId) === pageId)
    })
  }

  /**
   * Update just the title text of one sidebar item after a save.
   *
   * @param {number} pageId
   * @param {string} newTitle
   */
  updateSidebarTitle(pageId, newTitle) {
    const item = this.pageItemTargets.find(el => parseInt(el.dataset.pageId) === pageId)
    if (!item) return

    const titleEl = item.querySelector(".page-title")
    if (titleEl) {
      const iconHtml = titleEl.querySelector("i")?.outerHTML || ""
      titleEl.innerHTML = `${iconHtml}${this.escapeHtml(newTitle || "Untitled")}`
    }
  }

  // ====================================================================
  // EDITOR RENDERING — branching by page type
  // ====================================================================

  /**
   * Main entry point after loading a page. Shows the correct editor
   * sub-panel (gallery or rich_text) and hides everything else.
   */
  renderEditor() {
    if (!this.currentPage) {
      this.showEmptyState()
      return
    }

    // Reveal the overall editor area.
    if (this.hasEditorTarget) {
      this.editorTarget.style.display = "block"
      const emptyState = this.editorTarget.querySelector(".empty-state")
      if (emptyState) emptyState.remove()
    }

    // Hide page-type selector — we are now editing a real page.
    if (this.hasPageTypeSelectorTarget) {
      this.pageTypeSelectorTarget.style.display = "none"
    }

    // Populate shared title field.
    if (this.hasTitleGroupTarget) this.titleGroupTarget.style.display = "block"
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.value = this.currentPage.title || ""
    }

    // Show save button group.
    if (this.hasSaveGroupTarget) this.saveGroupTarget.style.display = "block"

    // Branch by page type.
    if (this.currentPage.page_type === "gallery") {
      this.showGalleryEditor()
    } else {
      this.showRichTextEditor()
    }
  }

  /** Show empty state placeholder when there are no pages. */
  showEmptyState() {
    if (!this.hasEditorTarget) return

    // Hide editor sub-panels.
    if (this.hasGalleryEditorTarget) this.galleryEditorTarget.style.display = "none"
    if (this.hasRichTextEditorTarget) this.richTextEditorTarget.style.display = "none"
    if (this.hasTitleGroupTarget) this.titleGroupTarget.style.display = "none"
    if (this.hasSaveGroupTarget) this.saveGroupTarget.style.display = "none"
    if (this.hasLayoutSelectorTarget) this.layoutSelectorTarget.style.display = "none"
    if (this.hasPageTypeSelectorTarget) this.pageTypeSelectorTarget.style.display = "none"

    // Show or create the empty state element.
    let emptyState = this.editorTarget.querySelector(".empty-state")
    if (!emptyState) {
      emptyState = document.createElement("div")
      emptyState.className = "empty-state text-center p-5"
      emptyState.innerHTML = `
        <i class="fas fa-folder-open fa-3x mb-3 text-muted"></i>
        <h4>No pages yet</h4>
        <p class="text-muted">Click "Add New Page" to create your first additional page.</p>
      `
      this.editorTarget.appendChild(emptyState)
    }
  }

  // ====================================================================
  // GALLERY EDITOR
  // ====================================================================

  /** Activate the gallery editor panel and render thumbnail grid. */
  showGalleryEditor() {
    if (this.hasRichTextEditorTarget) this.richTextEditorTarget.style.display = "none"
    if (this.hasGalleryEditorTarget) this.galleryEditorTarget.style.display = "block"

    // Wire up click-to-open-file-picker on the drop zone.
    if (this.hasGalleryDropZoneTarget) {
      // Remove any previously attached listener before adding a fresh one.
      if (this._galleryZoneClickHandler) {
        this.galleryDropZoneTarget.removeEventListener("click", this._galleryZoneClickHandler)
      }
      this._galleryZoneClickHandler = () => {
        if (this.hasGalleryUploadInputTarget) this.galleryUploadInputTarget.click()
      }
      this.galleryDropZoneTarget.addEventListener("click", this._galleryZoneClickHandler)
    }

    this.renderGalleryThumbnails()
    this.updateLayoutSelectorVisibility()
  }

  /**
   * Render the thumbnail grid from `this.currentPage.gallery_images`.
   * Called after initial load and after each upload or deletion.
   */
  renderGalleryThumbnails() {
    if (!this.hasGalleryImagesTarget) return

    const images = this.currentPage?.gallery_images || []

    if (images.length === 0) {
      this.galleryImagesTarget.innerHTML = ""
      return
    }

    const thumbnailsHtml = images.map(img => `
      <div class="gallery-thumbnail" style="position: relative; border: 1px solid #ddd; border-radius: 4px; overflow: hidden;">
        <img src="${this.escapeHtml(img.url)}"
             style="width: 100%; height: 120px; object-fit: cover;"
             alt="${this.escapeHtml(img.filename)}" />
        <div style="padding: 4px; font-size: 11px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;"
             title="${this.escapeHtml(img.filename)}">
          ${this.escapeHtml(img.filename)}
        </div>
        <button class="btn btn-sm btn-danger gallery-remove-btn"
                style="position: absolute; top: 4px; right: 4px; padding: 2px 6px; font-size: 10px;"
                type="button"
                data-action="click->additional-pages#removeGalleryImage"
                data-image-id="${img.id}"
                title="Remove image">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    `).join("")

    this.galleryImagesTarget.innerHTML = `
      <div class="gallery-thumbnail-grid"
           style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px;">
        ${thumbnailsHtml}
      </div>
    `
  }

  // ---------- Gallery drag-and-drop events ----------

  galleryDragOver(event) {
    event.preventDefault()
  }

  galleryDragEnter(event) {
    event.preventDefault()
    if (this.hasGalleryDropZoneTarget) {
      this.galleryDropZoneTarget.style.borderColor = "#5b73e8"
      this.galleryDropZoneTarget.style.background = "#f0f2ff"
    }
  }

  galleryDragLeave(event) {
    if (this.hasGalleryDropZoneTarget) {
      this.galleryDropZoneTarget.style.borderColor = "#ccc"
      this.galleryDropZoneTarget.style.background = "#fafafa"
    }
  }

  galleryDrop(event) {
    event.preventDefault()
    this.galleryDragLeave(event)

    const files = event.dataTransfer?.files
    if (files?.length > 0) {
      this.uploadGalleryImages(Array.from(files))
    }
  }

  /**
   * Triggered by the hidden file input's change event.
   *
   * @param {Event} event
   */
  handleGalleryFileSelect(event) {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    // Reset the input so the same file can be re-selected after removal.
    event.target.value = ""

    this.uploadGalleryImages(files)
  }

  /**
   * Validate and upload an array of File objects to the gallery endpoint.
   *
   * @param {File[]} files
   */
  async uploadGalleryImages(files) {
    const currentCount = this.currentPage?.gallery_images?.length || 0
    const maxAllowed = 9

    if (currentCount >= maxAllowed) {
      this.setStatus(`Maximum ${maxAllowed} images per gallery page reached.`, "error")
      return
    }

    const available = maxAllowed - currentCount
    if (files.length > available) {
      this.setStatus(
        `Can only add ${available} more image(s). You selected ${files.length}.`,
        "error"
      )
      files = files.slice(0, available)
    }

    // Basic client-side type validation.
    const accepted = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/tiff", "image/x-tiff", "application/dicom"]
    const acceptedExts = [".jpg", ".jpeg", ".png", ".webp", ".tif", ".tiff", ".dcm", ".dicom"]

    const invalidFiles = files.filter(file => {
      const ext = "." + (file.name || "").split(".").pop().toLowerCase()
      return !accepted.includes(file.type) && !acceptedExts.includes(ext)
    })

    if (invalidFiles.length > 0) {
      const names = invalidFiles.map(f => f.name).join(", ")
      this.setStatus(`Invalid file type(s): ${names}`, "error")
      return
    }

    // Show progress indicator.
    this.setGalleryUploadProgress(true, `Uploading ${files.length} image(s)...`)

    try {
      const formData = new FormData()
      files.forEach(file => formData.append("images[]", file))

      const response = await fetch(this.addGalleryImagesUrl(this.currentPage.id), {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
          // Do NOT set Content-Type for FormData — browser handles boundary automatically.
        },
        body: formData
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      // Merge updated page data (includes fresh gallery_images array).
      this.currentPage = data.page

      this.renderGalleryThumbnails()
      this.updateLayoutSelectorVisibility()
      this.setStatus("Images uploaded", "success")
      this.dispatchPagesChanged()

      setTimeout(() => this.setStatus("", ""), 2500)
    } catch (error) {
      console.error("[additional-pages] Gallery upload failed:", error)
      this.setStatus(`Upload failed: ${error.message}`, "error")
    } finally {
      this.setGalleryUploadProgress(false)
    }
  }

  /**
   * Remove a single gallery image via the delete endpoint.
   * Bound to each thumbnail's remove button via data-action.
   *
   * @param {Event} event
   */
  async removeGalleryImage(event) {
    const imageId = parseInt(event.currentTarget.dataset.imageId)
    if (!imageId) return

    try {
      const url = `${this.removeGalleryImageUrl(this.currentPage.id)}?image_id=${imageId}`
      const response = await fetch(url, {
        method: "DELETE",
        headers: this.headers()
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      // Refresh page data with updated gallery_images list.
      this.currentPage = data.page

      this.renderGalleryThumbnails()
      this.updateLayoutSelectorVisibility()
      this.setStatus("Image removed", "success")
      this.dispatchPagesChanged()

      setTimeout(() => this.setStatus("", ""), 2000)
    } catch (error) {
      console.error("[additional-pages] Remove gallery image failed:", error)
      this.setStatus(`Failed to remove image: ${error.message}`, "error")
    }
  }

  /**
   * Show or hide the gallery upload progress indicator.
   *
   * @param {boolean} show
   * @param {string}  [message]
   */
  setGalleryUploadProgress(show, message = "Uploading images...") {
    if (this.hasGalleryUploadProgressTarget) {
      this.galleryUploadProgressTarget.style.display = show ? "block" : "none"
    }
    if (show && this.hasGalleryUploadStatusTarget) {
      this.galleryUploadStatusTarget.textContent = message
    }
  }

  // ====================================================================
  // RICH TEXT EDITOR
  // ====================================================================

  /** Activate the rich text editor panel and load content into Trix. */
  showRichTextEditor() {
    if (this.hasGalleryEditorTarget) this.galleryEditorTarget.style.display = "none"
    if (this.hasRichTextEditorTarget) this.richTextEditorTarget.style.display = "block"

    this.loadTrixContent(this.currentPage.content || "")
    this.setupTrixChangeListener()
    this.updateLayoutSelectorVisibility()
  }

  /**
   * Load HTML into the Trix editor safely.
   * Waits for trix-initialize if the editor object is not yet ready.
   *
   * @param {string} html
   */
  loadTrixContent(html) {
    if (!this.hasTrixEditorTarget) return

    const doLoad = () => {
      this.isLoadingContent = true

      const trix = this.trixEditorTarget.editor
      trix.loadHTML("")

      // Give Trix a tick to settle after clearing, then set the real content.
      setTimeout(() => {
        if (html) trix.loadHTML(html)

        // Release the guard after all trix-change events triggered by loading have fired.
        setTimeout(() => {
          this.isLoadingContent = false
        }, 300)
      }, 50)
    }

    if (this.trixEditorTarget.editor) {
      doLoad()
    } else {
      const handler = () => {
        this.trixEditorTarget.removeEventListener("trix-initialize", handler)
        doLoad()
      }
      this.trixEditorTarget.addEventListener("trix-initialize", handler)
    }
  }

  /** Attach a trix-change listener. Safe to call multiple times. */
  setupTrixChangeListener() {
    if (!this.hasTrixEditorTarget || this._trixChangeListenerAttached) return

    this.trixEditorTarget.addEventListener("trix-change", () => {
      if (!this.isLoadingContent) this.markUnsaved()
    })

    this._trixChangeListenerAttached = true
  }

  // ====================================================================
  // PAGE TYPE SELECTOR (new page creation flow)
  // ====================================================================

  /**
   * Called when user clicks "Add New Page".
   * Shows the type selector card and hides the current editor content.
   */
  showPageTypeSelector() {
    if (this.hasPageTypeSelectorTarget) {
      this.pageTypeSelectorTarget.style.display = "block"
    }

    // Hide editor sub-panels while choosing type.
    if (this.hasGalleryEditorTarget) this.galleryEditorTarget.style.display = "none"
    if (this.hasRichTextEditorTarget) this.richTextEditorTarget.style.display = "none"
    if (this.hasTitleGroupTarget) this.titleGroupTarget.style.display = "none"
    if (this.hasSaveGroupTarget) this.saveGroupTarget.style.display = "none"
    if (this.hasLayoutSelectorTarget) this.layoutSelectorTarget.style.display = "none"
  }

  /**
   * Called when user clicks one of the type option cards.
   * Creates the page on the server with the chosen page_type.
   *
   * @param {Event} event
   */
  async createPageWithType(event) {
    const pageType = event.currentTarget.dataset.pageType
    if (!pageType) return

    try {
      this.setStatus("Creating new page...", "info")
      if (this.hasNewPageBtnTarget) this.newPageBtnTarget.disabled = true

      const response = await fetch(this.pagesIndexUrl(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          page: {
            title: pageType === "gallery" ? "Gallery Page" : "New Additional Page",
            content: "",
            page_type: pageType
          }
        })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      this.pages.push(data.page)
      this.renderPageList()
      await this.loadPageContent(data.page.id)
      this.setStatus("Page created", "success")
      this.dispatchPagesChanged()

      setTimeout(() => this.setStatus("", ""), 2000)
    } catch (error) {
      console.error("[additional-pages] Failed to create page:", error)
      this.setStatus(`Failed to create page: ${error.message}`, "error")
    } finally {
      if (this.hasNewPageBtnTarget) this.newPageBtnTarget.disabled = false
    }
  }

  // ====================================================================
  // PAGE SELECTION / SWITCHING
  // ====================================================================

  /**
   * Click handler for a page item in the sidebar.
   * Prompts to save unsaved changes before switching.
   *
   * @param {Event} event
   */
  async handlePageClick(event) {
    const pageId = parseInt(event.currentTarget.dataset.pageId)

    // Do nothing if already on this page.
    if (pageId === this.currentPageIdValue) return

    if (this.hasUnsavedChangesValue) {
      const save = confirm("You have unsaved changes. Save before leaving?")
      if (save) {
        await this.performSave()
      }
      // Whether saved or discarded, clear the flag before switching.
      this.hasUnsavedChangesValue = false
    }

    await this.loadPageContent(pageId)
  }

  // ====================================================================
  // SAVE SYSTEM (manual only — no auto-save)
  // ====================================================================

  /**
   * Action bound to the "Save Content" button.
   * Entry point for user-initiated saves.
   */
  async manualSave() {
    await this.performSave()
  }

  /**
   * Core save implementation. PATCHes the page data to the server.
   * For rich_text pages: sends title + content.
   * For gallery pages: sends title + image_layout only (images are managed separately).
   */
  async performSave() {
    if (!this.currentPage) return

    try {
      this.setStatus("Saving...", "info")

      const title = this.hasTitleInputTarget ? this.titleInputTarget.value : ""

      const pagePayload = { title, image_layout: this.currentPage.image_layout || "single" }

      if (this.currentPage.page_type === "rich_text" && this.hasContentInputTarget) {
        pagePayload.content = this.contentInputTarget.value
      }

      const response = await fetch(this.pageShowUrl(this.currentPage.id), {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ page: pagePayload })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      // Merge fresh data from server (preserves gallery_images, updated_at, etc.)
      this.currentPage = { ...this.currentPage, ...data.page }

      this.hasUnsavedChangesValue = false
      this.updateSidebarTitle(this.currentPage.id, data.page.title)
      this.setStatus("Saved", "success")
      this.dispatchPagesChanged()

      // Also update the preview tab if the server returned preview_html.
      if (data.page.preview_html) {
        const pageIndex = this.pages.findIndex(p => p.id === this.currentPage.id)
        this.updatePreviewTab(this.currentPage.id, data.page.title, data.page.preview_html, pageIndex)
      }

      setTimeout(() => {
        if (this.hasSaveStatusTarget && this.saveStatusTarget.textContent === "Saved") {
          this.setStatus("", "")
        }
      }, 2500)
    } catch (error) {
      console.error("[additional-pages] Save failed:", error)
      this.setStatus(`Failed to save: ${error.message}`, "error")
    }
  }

  /**
   * Mark the current page as having unsaved changes.
   * Updates the save button visual. Does not trigger any async work.
   */
  markUnsaved() {
    if (this.isLoadingContent) return

    this.hasUnsavedChangesValue = true
    this.setStatus("Unsaved changes", "warning")
    this.dispatch("changed", { detail: { hasUnsavedChanges: true } })
  }

  // ====================================================================
  // LAYOUT SELECTOR
  // ====================================================================

  /**
   * Action bound to each layout button (single column / grid).
   * Immediately PATCHes only the image_layout attribute.
   *
   * @param {Event} event
   */
  async setLayout(event) {
    const layout = event.currentTarget.dataset.layout
    if (!this.currentPage || !["single", "grid"].includes(layout)) return

    // Optimistically update UI.
    this.currentPage.image_layout = layout
    this.updateLayoutButtons(layout)

    try {
      this.setStatus("Saving layout...", "info")

      const response = await fetch(this.pageShowUrl(this.currentPage.id), {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ page: { image_layout: layout } })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      this.currentPage.image_layout = data.page.image_layout

      // Refresh preview tab with new layout rendering.
      if (data.page.preview_html) {
        const pageIndex = this.pages.findIndex(p => p.id === this.currentPage.id)
        this.updatePreviewTab(this.currentPage.id, data.page.title, data.page.preview_html, pageIndex)
      }

      this.setStatus("Layout saved", "success")
      setTimeout(() => this.setStatus("", ""), 2000)
    } catch (error) {
      console.error("[additional-pages] Layout save failed:", error)
      this.setStatus("Failed to save layout", "error")
    }
  }

  /**
   * Toggle active state on layout buttons to match `activeLayout`.
   *
   * @param {string} activeLayout  "single" or "grid"
   */
  updateLayoutButtons(activeLayout) {
    if (!this.hasLayoutSelectorTarget) return
    this.layoutSelectorTarget.querySelectorAll(".layout-btn").forEach(btn => {
      btn.classList.toggle("active", btn.dataset.layout === activeLayout)
    })
  }

  /**
   * Show the layout selector when images are present; hide it when there are none.
   * For gallery pages: any gallery images. For rich_text: any <img> in content.
   */
  updateLayoutSelectorVisibility() {
    if (!this.hasLayoutSelectorTarget) return

    let hasImages = false

    if (this.currentPage?.page_type === "gallery") {
      hasImages = (this.currentPage.gallery_images?.length || 0) > 0
    } else {
      const content = this.hasContentInputTarget ? this.contentInputTarget.value : ""
      hasImages = content.includes("<img") || content.includes("action-text-attachment")
    }

    this.layoutSelectorTarget.style.display = hasImages ? "block" : "none"

    if (hasImages) {
      this.updateLayoutButtons(this.currentPage?.image_layout || "single")
    }
  }

  // ====================================================================
  // DELETE PAGE
  // ====================================================================

  /**
   * Action bound to each sidebar delete button.
   *
   * @param {Event} event
   */
  async deletePage(event) {
    event.stopPropagation()

    const pageId = parseInt(event.currentTarget.dataset.pageId)
    const page = this.pages.find(p => p.id === pageId)
    if (!page) return

    if (!confirm(`Are you sure you want to delete "${page.title || "this page"}"?`)) return

    try {
      this.setStatus("Deleting page...", "info")

      const response = await fetch(this.pageShowUrl(pageId), {
        method: "DELETE",
        headers: this.headers()
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        const msg = data.errors ? data.errors.join(", ") : `HTTP ${response.status}`
        throw new Error(msg)
      }

      // Remove from local state.
      this.pages = this.pages.filter(p => p.id !== pageId)

      // Remove from preview tab if present.
      const previewCard = document.querySelector(`[data-preview-page-id="${pageId}"]`)
      if (previewCard) {
        previewCard.remove()
        const section = document.querySelector(".additional-pages-preview-section")
        if (section && !section.querySelector("[data-preview-page-id]")) section.remove()
      }

      // If the deleted page was active, select another one.
      if (this.currentPageIdValue === pageId) {
        this.currentPage = null
        this.currentPageIdValue = null

        if (this.pages.length > 0) {
          await this.loadPageContent(this.pages[0].id)
        } else {
          this.renderPageList()
          this.showEmptyState()
        }
      } else {
        this.renderPageList()
      }

      this.setStatus("Page deleted", "success")
      this.dispatchPagesChanged()

      setTimeout(() => this.setStatus("", ""), 2000)
    } catch (error) {
      console.error("[additional-pages] Delete page failed:", error)
      this.setStatus(`Failed to delete page: ${error.message}`, "error")
    }
  }

  // ====================================================================
  // DRAG-AND-DROP REORDERING (sortable)
  // ====================================================================

  /** Initialise SortableJS on the page list container. */
  initializeSortable() {
    if (this.sortable) this.sortable.destroy()
    if (!this.hasPageListTarget) return

    this.sortable = new Sortable(this.pageListTarget, {
      animation: 150,
      handle: ".page-drag-handle",
      ghostClass: "sortable-ghost",
      dragClass: "sortable-drag",
      onEnd: (event) => this.handleReorder(event)
    })
  }

  /**
   * Persist the new page order after a drag-and-drop operation.
   *
   * @param {SortableEvent} event
   */
  async handleReorder(event) {
    try {
      const positions = {}
      this.pageListTarget.querySelectorAll("[data-page-id]").forEach((item, index) => {
        positions[parseInt(item.dataset.pageId)] = index + 1
      })

      this.setStatus("Updating order...", "info")

      const response = await fetch(this.reorderUrl(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ positions })
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      // Sync local array order from server response.
      this.pages = data.pages

      this.setStatus("Order updated", "success")
      setTimeout(() => this.setStatus("", ""), 2000)
    } catch (error) {
      console.error("[additional-pages] Reorder failed:", error)
      this.setStatus(`Failed to update order: ${error.message}`, "error")

      // Restore visual order by re-rendering.
      this.renderPageList()
    }
  }

  // ====================================================================
  // PREVIEW TAB INTEGRATION
  // ====================================================================

  /**
   * Update (or create) the preview card for a page in the Preview tab.
   *
   * @param {number} pageId
   * @param {string} title
   * @param {string} previewHtml  Server-rendered HTML (trusted source).
   * @param {number} pageIndex    Zero-based position in the pages array.
   */
  updatePreviewTab(pageId, title, previewHtml, pageIndex) {
    // The preview container lives in fieldset_5 or a preview-panel element.
    const previewContainer = document.querySelector(
      "#fieldset_5 .preview-container, #preview-panel .preview-container"
    )
    if (!previewContainer) return

    const layout = this.currentPage?.image_layout || "single"
    const existingCard = previewContainer.querySelector(`[data-preview-page-id="${pageId}"]`)

    if (existingCard) {
      const titleEl = existingCard.querySelector(".card-header h5")
      const contentEl = existingCard.querySelector(".additional-page-content")

      if (titleEl) {
        titleEl.textContent = ""
        const icon = document.createElement("i")
        icon.className = "fas fa-file-alt mr-2"
        titleEl.appendChild(icon)
        titleEl.appendChild(document.createTextNode(this.escapeHtml(title) || `Additional Page ${pageIndex + 1}`))
      }
      if (contentEl) {
        contentEl.className = `additional-page-content layout-${layout}`
        contentEl.innerHTML = previewHtml
      }
    } else {
      let section = previewContainer.querySelector(".additional-pages-preview-section")

      if (!section) {
        section = document.createElement("div")
        section.className = "additional-pages-preview-section mt-4"
        section.innerHTML = '<hr class="my-4">'

        const actionButtons = previewContainer.querySelector(".row.mt-4.mb-5")
        if (actionButtons) {
          previewContainer.insertBefore(section, actionButtons)
        } else {
          previewContainer.appendChild(section)
        }
      }

      const card = document.createElement("div")
      card.className = "card mb-4 shadow-sm"
      card.setAttribute("data-preview-page-id", pageId)
      card.innerHTML = `
        <div class="card-header bg-warning text-dark">
          <h5 class="mb-0">
            <i class="fas fa-file-alt mr-2"></i>
            ${this.escapeHtml(title) || `Additional Page ${pageIndex + 1}`}
          </h5>
        </div>
        <div class="card-body">
          <div class="additional-page-content layout-${layout}"
               style="min-height: 200px; padding: 15px; background-color: #fafafa; border-radius: 5px;">
            ${previewHtml}
          </div>
        </div>
      `
      section.appendChild(card)
    }
  }

  /**
   * Dispatch the `additional-pages:changed` custom event to notify the preview
   * tab and any other listeners that page data has changed.
   */
  dispatchPagesChanged() {
    this.dispatch("changed", { detail: { hasUnsavedChanges: this.hasUnsavedChangesValue } })

    // Also dispatch a document-level event for non-Stimulus listeners.
    document.dispatchEvent(new CustomEvent("additional-pages:changed", {
      bubbles: true,
      detail: { reportId: this.reportIdValue, taskId: this.taskIdValue }
    }))
  }

  // ====================================================================
  // PUBLIC API (for tab navigation controller)
  // ====================================================================

  /** @returns {boolean} */
  hasUnsavedChanges() {
    return this.hasUnsavedChangesValue
  }

  /**
   * Save if there are pending changes. Intended for use by a parent controller
   * that needs to ensure state is persisted before navigating away.
   */
  async saveBeforeLeaving() {
    if (this.hasUnsavedChangesValue) {
      await this.performSave()
    }
  }

  // ====================================================================
  // STATUS / UI HELPERS
  // ====================================================================

  /**
   * Set the save status indicator text and style class.
   *
   * @param {string} message
   * @param {""|"info"|"success"|"warning"|"error"} type
   */
  setStatus(message, type) {
    if (!this.hasSaveStatusTarget) return

    this.saveStatusTarget.textContent = message
    this.saveStatusTarget.className = `save-status ${type}`
  }

  /**
   * Escape a string for safe insertion into HTML attribute values or text nodes.
   *
   * @param {string|null|undefined} text
   * @returns {string}
   */
  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // ====================================================================
  // URL HELPERS
  // ====================================================================

  /** Extract the locale prefix from the current URL path, defaulting to "nn". */
  getLocale() {
    const part = window.location.pathname.split("/")[1]
    return ["en", "nn"].includes(part) ? part : "nn"
  }

  /** @returns {string} */
  pagesIndexUrl() {
    const l = this.getLocale()
    return `/${l}/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/pages`
  }

  /**
   * @param {number} pageId
   * @returns {string}
   */
  pageShowUrl(pageId) {
    const l = this.getLocale()
    return `/${l}/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/pages/${pageId}`
  }

  /**
   * @param {number} pageId
   * @returns {string}
   */
  addGalleryImagesUrl(pageId) {
    return `${this.pageShowUrl(pageId)}/add_gallery_images`
  }

  /**
   * @param {number} pageId
   * @returns {string}
   */
  removeGalleryImageUrl(pageId) {
    return `${this.pageShowUrl(pageId)}/remove_gallery_image`
  }

  /** @returns {string} */
  reorderUrl() {
    return `${this.pagesIndexUrl()}/reorder`
  }

  /**
   * Standard JSON request headers (includes CSRF token).
   *
   * @returns {Object}
   */
  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  /** @returns {string} */
  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
